import { useNavigate, useParams } from "react-router-dom";
import { CircularProgress, IconButton } from "@mui/material";
import { toast } from "react-toastify";
import { useGetRegisterById } from "@services/registers/getById";
import ListPreview from "@commons/Preview/ListPreview";
import { prepareRegistersView } from "@utils/translator/registers";
import Heading from "@commons/Typography/Heading";
import { useEffect, useState } from "react";
import { config } from "@/config";
import { useGetRegisterAttachments } from "@services/registers/getAttachmentsById";
import Text from "@commons/Typography/Text";
import AttachmentPreview from "@commons/Preview/AttachmentPreview";
import Button from "@commons/Button";
import PageviewIcon from "@mui/icons-material/Pageview";
import { usePermissions } from "@hooks/usePermissions";
import { usePage } from "@hooks/usePage";
import ModalEditRegister from "../components/modal/ModalEditRegister";

function ViewRegister() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const { can } = usePermissions();
  const { openModal, setDataModal } = usePage();

  const {
    data: attachments,
    isLoading: isLoadingAttachments,
    error: attachmentsError,
  } = useGetRegisterAttachments(id ?? "", {
    page: currentPage,
    limit: config.attachmentsGallery.defaultPageSize,
  });

  const { data: register, isLoading, error } = useGetRegisterById(id ?? null);

  const processedRegister = register ? prepareRegistersView(register) : null;

  const handleEdit = () => {
    setDataModal({ id });
    openModal("editRegister");
  };

  useEffect(() => {
    if (error) {
      toast.error("Erro ao carregar os dados do registro.");
      navigate("/registers");
    }
  }, [error, navigate]);

  useEffect(() => {
    if (!isLoading && !register && !error) {
      toast.warning("Registro não encontrado.");
      navigate("/registers");
    }
  }, [isLoading, register, error, navigate]);

  const canUpdateRegister = can("update", "registers");

  return (
    <section className="flex flex-col gap-2 items-start w-full bg-white justify-start p-4 sm:p-6 shadow-md rounded-2xl">
      <div className="mb-4 flex flex-col sm:flex-row flex-wrap sm:items-center sm:justify-between w-full gap-4">
        <Heading className="mb-2 sm:mb-0">Detalhes do Registro</Heading>

        <nav className="flex flex-col xs:flex-row gap-2 lg:gap-4 w-full sm:w-auto">
          <Button
            customVariant="primary"
            onClick={() => navigate("/registers")}
          >
            <Text size="sm">Voltar</Text>
          </Button>

          {canUpdateRegister && (
            <Button customVariant="muted" onClick={handleEdit}>
              <Text size="sm">Editar</Text>
            </Button>
          )}
        </nav>
      </div>

      <div className="w-full">
        {isLoading ? (
          <div className="flex justify-center items-center h-64">
            <CircularProgress size={60} className="text-primary" />
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="flex flex-col gap-2 items-start w-full h-full justify-start p-4 rounded-2xl border border-gray-200">
              <Heading size="sm">Informações Gerais</Heading>
              <ListPreview.Root>
                <ListPreview.Row
                  label="Tipo de Registro:"
                  value={processedRegister?.type || "—"}
                />
                <ListPreview.Row
                  label="Tipo de Ambiente:"
                  value={processedRegister?.environment || "—"}
                />
                <ListPreview.Row
                  label="Data do Registro:"
                  value={processedRegister?.registerDate || "—"}
                />
                <ListPreview.Row
                  label="Coletor:"
                  value={processedRegister?.reporter?.username || "—"}
                />

                <ListPreview.Row
                  label="Última Edição por:"
                  value={processedRegister?.lastEditor || "—"}
                />
              </ListPreview.Root>
            </div>
            <div className="flex flex-col gap-2 items-start w-full h-full justify-start p-4 rounded-2xl border border-gray-200">
              <div className="relative w-full">
                <Heading size="sm">Associações</Heading>
                <IconButton
                  className="absolute -top-2 left-28"
                  onClick={() =>
                    navigate(
                      "/individuals/" +
                        (processedRegister?.individual?.id || ""),
                    )
                  }
                  disabled={!processedRegister?.individual}
                >
                  <PageviewIcon
                    className={`${!processedRegister?.individual ? "opacity-70" : "text-primary"}`}
                  />
                </IconButton>
              </div>
              <ListPreview.Root>
                {processedRegister?.individual && (
                  <>
                    <ListPreview.Row
                      label="Indivíduo Associado:"
                      value={`${processedRegister.individual.commonName}, ID: ${processedRegister.individual.clinicalRecord}`}
                    />
                    <ListPreview.Row
                      label="Espécie:"
                      value={
                        processedRegister.individual.taxonomicGroup.species
                      }
                    />
                  </>
                )}
                {(processedRegister?.anonymousIndividualTaxonomicGroup ||
                  processedRegister?.anonymousIndividualGender ||
                  processedRegister?.anonymousGroupMembersNumber) && (
                  <>
                    <ListPreview.Row
                      label="Espécie:"
                      value={
                        processedRegister?.anonymousIndividualTaxonomicGroup
                          ?.species || "—"
                      }
                    />
                    <ListPreview.Row
                      label="Gênero:"
                      value={
                        processedRegister?.anonymousIndividualGender || "—"
                      }
                    />
                    <ListPreview.Row
                      label="Quantidade de Animais:"
                      value={
                        processedRegister?.anonymousGroupMembersNumber || "—"
                      }
                    />
                  </>
                )}
              </ListPreview.Root>
            </div>

            <div className="flex flex-col lg:col-span-2 gap-2 items-start w-full h-full justify-start p-4 rounded-2xl border border-gray-200 overflow-x-auto">
              <Heading size="sm">Anexos</Heading>

              {isLoadingAttachments && (
                <div className="flex justify-center items-center py-4">
                  <CircularProgress size={24} className="text-primary" />
                  <Text size="sm" className="ml-2 text-gray-500">
                    Carregando anexos...
                  </Text>
                </div>
              )}

              {attachmentsError && (
                <div className="flex items-center py-2">
                  <Text size="sm" className="text-red-500">
                    Erro ao carregar anexos. Tente novamente.
                  </Text>
                </div>
              )}

              {!isLoadingAttachments &&
                !attachmentsError &&
                attachments &&
                attachments?.items.length > 0 && (
                  <AttachmentPreview
                    attachments={attachments.items}
                    pagination={{
                      pageSize: config.attachmentsGallery.defaultPageSize,
                      currentPage: attachments.meta.currentPage,
                      totalPages: attachments.meta.totalPages,
                      totalItems: attachments.meta.totalItems,
                      onPageChange: setCurrentPage,
                    }}
                  />
                )}

              {!isLoadingAttachments &&
                !attachmentsError &&
                (!attachments || attachments?.items.length === 0) && (
                  <Text size="sm" className="text-gray-500 py-2">
                    Nenhum anexo encontrado.
                  </Text>
                )}
            </div>

            <div className="flex flex-col lg:col-span-2 gap-2 items-start w-full h-full justify-start p-4 rounded-2xl border border-gray-200">
              <Heading size="sm">Observações</Heading>
              {processedRegister?.observations ? (
                <Text size="sm" className="text-gray-700 whitespace-pre-wrap">
                  {processedRegister.observations}
                </Text>
              ) : (
                <Text size="sm" className="text-gray-500 py-2">
                  Nenhuma observação encontrada.
                </Text>
              )}
            </div>
          </div>
        )}
      </div>
      <ModalEditRegister />
    </section>
  );
}

export default ViewRegister;
